import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import SetPinPage from './pages/auth/SetPinPage';
import FareTestPage from './pages/admin/FareTestPage';

// ═══════════════ AUTH PAGES ═══════════════
import LoginPage from './pages/auth/LoginPage';
import RiderRegisterPage from './pages/auth/RiderRegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import OtpVerificationPage from './pages/auth/OtpVerificationPage';
import AdminPinPage from './pages/auth/AdminPinPage';

// ═══════════════ RIDER PAGES ═══════════════
import RiderDashboard from './pages/rider/RiderDashboard';
import RiderWallet from './pages/rider/RiderWallet';
import DriverWallet from './pages/driver/DriverWallet';
import BookingWizard from './pages/rider/BookingWizard';
import ActiveTrip from './pages/rider/ActiveTrip';
import TripHistory from './pages/rider/TripHistory';
import SeriesList from './pages/rider/SeriesList';
import SeriesDetail from './pages/rider/SeriesDetail';
import VehicleGroupsList from './pages/rider/VehicleGroupsList';
import VehicleGroupStatus from './pages/rider/VehicleGroupStatus';
// Compliance pages (Batch 10)
import SubmitComplaint from './pages/rider/SubmitComplaint';
import MyComplaints from './pages/rider/MyComplaints';
import ReportIncident from './pages/driver/ReportIncident';
import AdminIncidents from './pages/admin/AdminIncidents';
import AdminComplaints from './pages/admin/AdminComplaints';
import AdminAlertsTab from './pages/admin/AdminAlertsTab';
import AdminLevyPanel from './pages/admin/AdminLevyPanel';
import RouteDeviationsReview from './pages/admin/RouteDeviationsReview';
import DriverTaxSummary from './pages/driver/DriverTaxSummary';


// Removed legacy ReportLostItem —” replaced by Batch 14 (imported below)
import { RateTrip, SupportTicket } from './pages/rider/RiderSupportPages';
import RiderSettings from './pages/rider/RiderSettings';
import DriverSettings from './pages/driver/DriverSettings';

// ═══════════════ DRIVER PAGES ═══════════════
import DriverDashboard from './pages/driver/DriverDashboard';
import AvailableBookings from './pages/driver/AvailableBookings';
import DriverRegistrationWizard from './pages/driver/DriverRegistrationWizard';
import DriverApplicationStatus from './pages/driver/DriverApplicationStatus';
import DriverApplicationUpdate from './pages/driver/DriverApplicationUpdate';
import TripManagement from './pages/driver/TripManagement';
import DriverSchedule from './pages/driver/DriverSchedule';
import TrainingModules, { QuizPage } from './pages/driver/TrainingModules';
import PreTripChecklist, { FatigueStatus } from './pages/driver/PreTripChecklist';
// Legacy DriverReportPages —” being replaced by Batch 14 (rebuilt in later messages)
// Imports removed: ReportFoundItem, ReportIncident, DamageClaim
// Lost items —” rider (Batch 14)
import ReportLostItem from './pages/rider/ReportLostItem';
import MyLostItems from './pages/rider/MyLostItems';
import LostItemDetail from './pages/rider/LostItemDetail';
// Lost items —” driver (Batch 14)
import MyLostItemCases from './pages/driver/MyLostItemCases';
import LostItemDetailDriver from './pages/driver/LostItemDetailDriver';
import ReportFoundItem from './pages/driver/ReportFoundItem';
// Damage claims (Batch 14)
import SubmitDamageClaim from './pages/driver/SubmitDamageClaim';
import MyDamageClaims from './pages/driver/MyDamageClaims';
import DamageClaimDetail from './pages/driver/DamageClaimDetail';
import MyChargedClaims from './pages/rider/MyChargedClaims';
import ChargedClaimDetail from './pages/rider/ChargedClaimDetail';


// Admin dispute pages (Batch 14)
import AdminLostItems from './pages/admin/AdminLostItems';
import AdminClaims from './pages/admin/AdminClaims';
// Batch 16 M1 —” Document expiry
import MyDocuments from './pages/driver/MyDocuments';
import AdminDocumentExpiry from './pages/admin/AdminDocumentExpiry';
// Batch 16 M2 —” Annual recheck
import MyRecheck from './pages/driver/MyRecheck';
import AdminRecheck from './pages/admin/AdminRecheck';
// Batch 16 M3 —” Admin MFA
import AdminMfaSetup from './pages/admin/AdminMfaSetup';



// ═══════════════ ADMIN PAGES ═══════════════
import AdminLayout from './pages/admin/AdminLayout';
import { AdminOverview, BookingsPanel, DriversPanel, RidersPanel, VehiclesPanel } from './pages/admin/AdminCorePanels';
import { SupportCentre, LostItemsPanel, DamageClaimsPanel, SafetyDashboard, FareConfigPanel, FinancialReports, AuditLogPanel, FlaggedMessagesPanel } from './pages/admin/AdminOperationsPanels';
import DriverApplicationDetail from './pages/admin/DriverApplicationDetail';
import { PromoCodesPanel, ZonesPanel, TrainingModulesPanel, SystemSettingsPanel, FareSettingsPanel } from './pages/admin/AdminConfigPanels';
import { FatigueOverviewPanel, RatingsOverviewPanel, CorporateAccountsPanel } from './pages/admin/AdminMonitoringPanels';
import AnnualDistributionPanel from './pages/admin/AnnualDistributionPanel';
import DriverRewards from './pages/driver/DriverRewards';

// ═══════════════ COMMON PAGES ═══════════════
import { LegalDocuments, OnboardingTutorial } from './pages/common/CommonPages';
import CompliancePolicies from './pages/common/CompliancePolicies';

// ═══════════════ COMPONENTS ═══════════════
import PWAInstallPrompt from './components/common/PWAInstallPrompt';
import MaintenanceGate from './components/common/MaintenanceGate';

// ═══════════════ PROTECTED ROUTE ═══════════════
function ProtectedRoute({ children, allowedRoles = [] }) {
  const { currentUser, userProfile, loading } = useAuth();

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: '#94a3b8' }}>Loading...</div>;
  if (!currentUser) return <Navigate to="/login" />;
  if (allowedRoles.length > 0 && userProfile && !allowedRoles.includes(userProfile.role)) {
    // Redirect to appropriate dashboard based on role
    if (userProfile.role === 'rider') return <Navigate to="/rider" />;
    if (userProfile.role === 'driver') return <Navigate to="/driver" />;
    if (['admin', 'superAdmin'].includes(userProfile.role)) return <Navigate to="/admin" />;
    return <Navigate to="/login" />;
  }

  // Gate: a driver may only reach operational pages once their application is APPROVED
  // (admin sets user status to 'active'). Until then — pending, under review, changes
  // requested, rejected, suspended, deactivated — send them to their status page.
  if (userProfile && userProfile.role === 'driver' && userProfile.status !== 'active') {
    return <Navigate to="/driver/application-status" replace />;
  }

  return children;
}

// ═══════════════ ROLE-BASED REDIRECT ═══════════════
function RoleRedirect() {
  const { userProfile, loading } = useAuth();

  if (loading) return null;
  if (!userProfile) return <Navigate to="/login" />;

  switch (userProfile.role) {
    case 'rider': return <Navigate to="/rider" />;
    case 'driver': return <Navigate to="/driver" />;
    case 'admin':
    case 'superAdmin': return <Navigate to="/admin" />;
    default: return <Navigate to="/login" />;
  }
}

// ═══════════════ MAIN APP ═══════════════
function AppRoutes() {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const { currentUser, userProfile } = useAuth();

  useEffect(() => {
    // Onboarding tutorial is the RIDER welcome carousel. Only riders should ever see it —
    // never drivers (whose account is created at registration submit, which would otherwise
    // pop this over the wizard) or admins.
    if (
      currentUser &&
      userProfile?.role === 'rider' &&
      userProfile?.status === 'active' &&
      !localStorage.getItem('reservo_onboarding_complete')
    ) {
      setShowOnboarding(true);
    } else {
      setShowOnboarding(false);
    }
  }, [currentUser, userProfile]);

  return (
    <>
      {showOnboarding && <OnboardingTutorial onComplete={() => setShowOnboarding(false)} />}
      <PWAInstallPrompt />
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RiderRegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/verify-otp" element={<OtpVerificationPage />} />
        <Route path="/admin-pin" element={<AdminPinPage />} />
        <Route path="/set-pin" element={<SetPinPage />} />
        <Route path="/driver/register" element={<DriverRegistrationWizard />} />
        <Route path="/driver/register/:inviteId" element={<DriverRegistrationWizard />} />
        <Route path="/driver/application-status" element={<DriverApplicationStatus />} />
        <Route path="/driver/update-application" element={<DriverApplicationUpdate />} />
        <Route path="/legal" element={<LegalDocuments />} />
        <Route path="/legal/:section" element={<LegalDocuments />} />
        <Route path="/compliance" element={<CompliancePolicies />} />
        <Route path="/compliance/:policyId" element={<CompliancePolicies />} />
        <Route path="/admin/fare-test" element={<FareTestPage />} />
        {/* Role redirect */}
        <Route path="/" element={<RoleRedirect />} />

        {/* ═══════════ RIDER ROUTES ═══════════ */}
        <Route path="/rider" element={<ProtectedRoute allowedRoles={['rider']}><RiderDashboard /></ProtectedRoute>} />
        <Route path="/rider/wallet" element={<ProtectedRoute allowedRoles={['rider']}><RiderWallet /></ProtectedRoute>} />
        <Route path="/driver/wallet" element={<ProtectedRoute allowedRoles={['driver']}><DriverWallet /></ProtectedRoute>} />
        <Route path="/rider/book" element={<ProtectedRoute allowedRoles={['rider']}><BookingWizard /></ProtectedRoute>} />
        <Route path="/rider/trip/:bookingId" element={<ProtectedRoute allowedRoles={['rider']}><ActiveTrip /></ProtectedRoute>} />
        <Route path="/rider/trips" element={<ProtectedRoute allowedRoles={['rider']}><TripHistory /></ProtectedRoute>} />
        <Route path="/rider/series" element={<ProtectedRoute allowedRoles={['rider']}><SeriesList /></ProtectedRoute>} />
        <Route path="/rider/series/:seriesId" element={<ProtectedRoute allowedRoles={['rider']}><SeriesDetail /></ProtectedRoute>} />
        <Route path="/rider/vehicle-groups" element={<ProtectedRoute allowedRoles={['rider']}><VehicleGroupsList /></ProtectedRoute>} />
        <Route path="/rider/vehicle-group/:groupId" element={<ProtectedRoute allowedRoles={['rider']}><VehicleGroupStatus /></ProtectedRoute>} />

        {/* Compliance routes (Batch 10) */}
        <Route path="/rider/complaint/new" element={<ProtectedRoute allowedRoles={['rider']}><SubmitComplaint /></ProtectedRoute>} />
        <Route path="/rider/complaints" element={<ProtectedRoute allowedRoles={['rider']}><MyComplaints /></ProtectedRoute>} />
        <Route path="/driver/report-incident" element={<ProtectedRoute allowedRoles={['driver', 'rider']}><ReportIncident /></ProtectedRoute>} />
        <Route path="/admin/incidents" element={<ProtectedRoute allowedRoles={['admin']}><AdminIncidents /></ProtectedRoute>} />
        <Route path="/admin/incidents/:incidentId" element={<ProtectedRoute allowedRoles={['admin']}><AdminIncidents /></ProtectedRoute>} />
        <Route path="/admin/complaints" element={<ProtectedRoute allowedRoles={['admin']}><AdminComplaints /></ProtectedRoute>} />
        <Route path="/admin/complaints/:complaintId" element={<ProtectedRoute allowedRoles={['admin']}><AdminComplaints /></ProtectedRoute>} />
	<Route path="/admin/alerts" element={<ProtectedRoute allowedRoles={['admin']}><AdminAlertsTab /></ProtectedRoute>} />
        <Route path="/admin/levy" element={<ProtectedRoute allowedRoles={['admin']}><AdminLevyPanel /></ProtectedRoute>} />
	<Route path="/admin/levy/:monthKey" element={<ProtectedRoute allowedRoles={['admin']}><AdminLevyPanel /></ProtectedRoute>} />
        <Route path="/rider/rate/:bookingId" element={<ProtectedRoute allowedRoles={['rider']}><RateTrip /></ProtectedRoute>} />

        {/* Lost items —” rider (Batch 14) */}
        <Route path="/rider/report-lost-item" element={<ProtectedRoute allowedRoles={['rider']}><ReportLostItem /></ProtectedRoute>} />
        <Route path="/rider/lost-items" element={<ProtectedRoute allowedRoles={['rider']}><MyLostItems /></ProtectedRoute>} />
        <Route path="/rider/lost-items/:caseId" element={<ProtectedRoute allowedRoles={['rider']}><LostItemDetail /></ProtectedRoute>} />

        {/* Lost items —” driver (Batch 14) */}
        <Route path="/driver/lost-items" element={<ProtectedRoute allowedRoles={['driver']}><MyLostItemCases /></ProtectedRoute>} />
        <Route path="/driver/lost-items/:caseId" element={<ProtectedRoute allowedRoles={['driver']}><LostItemDetailDriver /></ProtectedRoute>} />
        <Route path="/driver/report-found-item" element={<ProtectedRoute allowedRoles={['driver']}><ReportFoundItem /></ProtectedRoute>} />

        {/* Damage claims —” driver (Batch 14) */}
        <Route path="/driver/submit-damage-claim" element={<ProtectedRoute allowedRoles={['driver']}><SubmitDamageClaim /></ProtectedRoute>} />
        <Route path="/driver/claims" element={<ProtectedRoute allowedRoles={['driver']}><MyDamageClaims /></ProtectedRoute>} />
        <Route path="/driver/claims/:claimId" element={<ProtectedRoute allowedRoles={['driver']}><DamageClaimDetail /></ProtectedRoute>} />

        {/* Damage claims —” rider (Batch 14) */}
        <Route path="/rider/claims" element={<ProtectedRoute allowedRoles={['rider']}><MyChargedClaims /></ProtectedRoute>} />
        <Route path="/rider/claims/:claimId" element={<ProtectedRoute allowedRoles={['rider']}><ChargedClaimDetail /></ProtectedRoute>} />



        <Route path="/rider/support" element={<ProtectedRoute allowedRoles={['rider']}><SupportTicket /></ProtectedRoute>} />
	<Route path="/rider/support/:bookingId" element={<ProtectedRoute allowedRoles={['rider']}><SupportTicket /></ProtectedRoute>} />
        <Route path="/rider/settings" element={<ProtectedRoute allowedRoles={['rider']}><RiderSettings /></ProtectedRoute>} />

        {/* ═══════════ DRIVER ROUTES ═══════════ */}
        <Route path="/driver" element={<ProtectedRoute allowedRoles={['driver']}><DriverDashboard /></ProtectedRoute>} />
        <Route path="/driver/available" element={<ProtectedRoute allowedRoles={['driver']}><AvailableBookings /></ProtectedRoute>} />
        <Route path="/driver/trip/:bookingId" element={<ProtectedRoute allowedRoles={['driver']}><TripManagement /></ProtectedRoute>} />
        <Route path="/driver/schedule" element={<ProtectedRoute allowedRoles={['driver']}><DriverSchedule /></ProtectedRoute>} />
        <Route path="/driver/trips" element={<ProtectedRoute allowedRoles={['driver']}><TripHistory /></ProtectedRoute>} />
        <Route path="/driver/rate/:bookingId" element={<ProtectedRoute allowedRoles={['driver']}><RateTrip /></ProtectedRoute>} />
        {/* Driver: found item / incident / claim routes —” to be rebuilt in Batch 14 Messages 4-5 */}
        {/* Note: /driver/incident/:bookingId already redirects via /driver/report-incident (Batch 10) */}
        <Route path="/driver/training" element={<ProtectedRoute allowedRoles={['driver']}><TrainingModules /></ProtectedRoute>} />

        <Route path="/driver/training/:moduleId" element={<ProtectedRoute allowedRoles={['driver']}><QuizPage /></ProtectedRoute>} />
        <Route path="/driver/checklist" element={<ProtectedRoute allowedRoles={['driver']}><PreTripChecklist /></ProtectedRoute>} />
        <Route path="/driver/fatigue" element={<ProtectedRoute allowedRoles={['driver']}><FatigueStatus /></ProtectedRoute>} />
        <Route path="/driver/rewards" element={<ProtectedRoute allowedRoles={['driver']}><DriverRewards /></ProtectedRoute>} />
        <Route path="/driver/settings" element={<ProtectedRoute allowedRoles={['driver']}><DriverSettings /></ProtectedRoute>} />
        <Route path="/driver/tax-summary" element={<ProtectedRoute allowedRoles={['driver']}><DriverTaxSummary /></ProtectedRoute>} />
	{/* Batch 16 M1 + M2 —” Compliance self-service */}
        <Route path="/driver/my-documents" element={<ProtectedRoute allowedRoles={['driver']}><MyDocuments /></ProtectedRoute>} />
        <Route path="/driver/my-recheck" element={<ProtectedRoute allowedRoles={['driver']}><MyRecheck /></ProtectedRoute>} />
        <Route path="/driver/support" element={<ProtectedRoute allowedRoles={['driver']}><SupportTicket /></ProtectedRoute>} />
        <Route path="/driver/support/:bookingId" element={<ProtectedRoute allowedRoles={['driver']}><SupportTicket /></ProtectedRoute>} />


        {/* ═══════════ ADMIN ROUTES ═══════════ */}
        <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin', 'superAdmin']}><AdminLayout /></ProtectedRoute>}>
          <Route index element={<AdminOverview />} />
          <Route path="bookings" element={<BookingsPanel />} />
          <Route path="drivers" element={<DriversPanel />} />
          <Route path="drivers/applications/:userId" element={<DriverApplicationDetail />} />

          <Route path="riders" element={<RidersPanel />} />
          <Route path="vehicles" element={<VehiclesPanel />} />
          <Route path="fares" element={<FareConfigPanel />} />
          <Route path="fares" element={<FareSettingsPanel />} />
          <Route path="zones" element={<ZonesPanel />} />
          <Route path="promos" element={<PromoCodesPanel />} />
          <Route path="support" element={<SupportCentre />} />
          <Route path="lost-items" element={<AdminLostItems />} />
          <Route path="lost-items/:caseId" element={<AdminLostItems />} />
          <Route path="claims" element={<AdminClaims />} />
          <Route path="claims/:claimId" element={<AdminClaims />} />
          <Route path="safety" element={<SafetyDashboard />} />
          <Route path="fatigue" element={<FatigueOverviewPanel />} />
          <Route path="training" element={<TrainingModulesPanel />} />
          <Route path="ratings" element={<RatingsOverviewPanel />} />
          <Route path="finance" element={<FinancialReports />} />
          <Route path="corporate" element={<CorporateAccountsPanel />} />
          <Route path="deviations" element={<RouteDeviationsReview />} />
          <Route path="settings" element={<SystemSettingsPanel />} />
          <Route path="distribution" element={<AnnualDistributionPanel />} />
          <Route path="audit-log" element={<AuditLogPanel />} />
          <Route path="flagged-messages" element={<FlaggedMessagesPanel />} />
          {/* Batch 16 M1, M2, M3 */}
          <Route path="document-expiry" element={<AdminDocumentExpiry />} />
          <Route path="recheck" element={<AdminRecheck />} />
          <Route path="mfa-setup" element={<AdminMfaSetup />} />
        </Route>


        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <MaintenanceGate>
          <AppRoutes />
        </MaintenanceGate>
        <Toaster position="top-center" toastOptions={{ duration: 4000, style: { borderRadius: 12, padding: '14px 24px', fontSize: '1rem', fontWeight: 600, maxWidth: 420, textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' } }} />

      </AuthProvider>
    </Router>
  );
}